import { pushGtmEvent, GTM_EVENT_NEWSLETTER_SUBSCRIBE } from "@/lib/gtm";

/**
 * Subscribe an email to the newsletter via HubSpot
 * @param email - The email address to subscribe
 * @returns Promise that resolves to the API response
 */
export async function subscribeToNewsletter(
  email: string,
  pathname?: string
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch("/api/hubspot/subscribe-newsletter", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, pathname }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to subscribe");
    }

    pushGtmEvent({
      event: GTM_EVENT_NEWSLETTER_SUBSCRIBE,
      form_id: "newsletter_subscribe",
    });

    return {
      success: true,
      message: data.message || "Successfully subscribed to newsletter",
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Something went wrong",
    };
  }
}

export const HUBSPOT_TIMEZONES = [
  {
    group: "US",
    zones: [
      { value: "US/Pacific", label: "UTC -08:00 Pacific Time" },
      { value: "US/Mountain", label: "UTC -07:00 Mountain Time" },
      { value: "US/Central", label: "UTC -06:00 Central Time" },
      { value: "US/East-Indiana", label: "UTC -05:00 Eastern Time" },
    ],
  },
  {
    group: "Europe",
    zones: [
      { value: "Europe/Belfast", label: "UTC +00:00 Dublin, London, Lisbon" },
      {
        value: "Europe/Amsterdam",
        label: "UTC +01:00 (Europe) Central European Time",
      },
      {
        value: "Europe/Athens",
        label: "UTC +02:00 Eastern European Time",
      },
      { value: "Europe/Chisinau", label: "UTC +02:00 Chisinau, Tiraspol" },
      { value: "Europe/Kaliningrad", label: "UTC +02:00 Kaliningrad" },
      {
        value: "Europe/Istanbul",
        label: "UTC +03:00 Istanbul, Kirov, Minsk, Moscow",
      },
      {
        value: "Europe/Astrakhan",
        label: "UTC +04:00 Astrakhan, Samara, Saratov, Ulyanovsk",
      },
    ],
  },
  {
    group: "Canada",
    zones: [
      { value: "Canada/Pacific", label: "UTC -08:00 Pacific" },
      { value: "Canada/Mountain", label: "UTC -07:00 Mountain" },
      { value: "Canada/Yukon", label: "UTC -07:00 Yukon" },
      { value: "Canada/Central", label: "UTC -06:00 Central" },
      { value: "Canada/Saskatchewan", label: "UTC -06:00 Saskatchewan" },
      { value: "Canada/Eastern", label: "UTC -05:00 Eastern" },
      { value: "Canada/Atlantic", label: "UTC -04:00 Atlantic Time" },
      { value: "Canada/Newfoundland", label: "UTC -03:30 Newfoundland" },
    ],
  },
  {
    group: "Africa",
    zones: [
      { value: "Africa/Abidjan", label: "UTC +00:00 Greenwich Mean Time" },
      {
        value: "Africa/Algiers",
        label: "UTC +01:00 (Africa) Central European Time",
      },
      { value: "Africa/Casablanca", label: "UTC +01:00 Casablanca, El Aaiun" },
      { value: "Africa/Ceuta", label: "UTC +01:00 Ceuta" },
      { value: "Africa/Blantyre", label: "UTC +02:00 South Africa Time" },
      { value: "Africa/Cairo", label: "UTC +02:00 Cairo" },
      { value: "Africa/Addis_Ababa", label: "UTC +03:00 East Africa Time" },
    ],
  },
  {
    group: "America",
    zones: [
      { value: "America/Adak", label: "UTC -10:00 Adak, Atka" },
      {
        value: "America/Anchorage",
        label: "UTC -09:00 Anchorage, Juneau, Metlakatla, Nome",
      },
      { value: "America/Ensenada", label: "UTC -08:00 Pacific Time" },
      { value: "America/Boise", label: "UTC -07:00 Mountain Time" },
      {
        value: "America/Creston",
        label: "UTC -07:00 Arizona & Yukon Time",
      },
      {
        value: "America/Bahia_Banderas",
        label: "UTC -06:00 Central Standard Time",
      },
      { value: "America/Chicago", label: "UTC -06:00 Central Time" },
      {
        value: "America/Atikokan",
        label: "UTC -05:00 Jamaica, Panama, Colombia",
      },
      { value: "America/Detroit", label: "UTC -05:00 Eastern Time" },
      { value: "America/Havana", label: "UTC -05:00 Havana" },
      {
        value: "America/Anguilla",
        label: "UTC -04:00 Eastern Caribbean Time",
      },
      {
        value: "America/Glace_Bay",
        label: "UTC -04:00 Glace Bay, Goose Bay, Halifax, Moncton",
      },
      { value: "America/Araguaina", label: "UTC -03:00 Argentina, Brazil" },
      { value: "America/Miquelon", label: "UTC -03:00 Miquelon" },
      { value: "America/Santiago", label: "UTC -03:00 Santiago" },
      { value: "America/St_Johns", label: "UTC -03:30 St Johns" },
      {
        value: "America/Godthab",
        label: "UTC -02:00 Godthab, Nuuk, Scoresbysund",
      },
      { value: "America/Noronha", label: "UTC -02:00 Noronha" },
      { value: "America/Danmarkshavn", label: "UTC +00:00 Danmarkshavn" },
    ],
  },
  {
    group: "Antarctica",
    zones: [
      { value: "Antarctica/Palmer", label: "UTC -03:00 Palmer, Rothera" },
      { value: "Antarctica/Troll", label: "UTC +00:00 Troll" },
      { value: "Antarctica/Syowa", label: "UTC +03:00 Syowa" },
      { value: "Antarctica/Mawson", label: "UTC +05:00 Mawson, Vostok" },
      { value: "Antarctica/Davis", label: "UTC +07:00 Davis" },
      { value: "Antarctica/Casey", label: "UTC +08:00 Casey" },
      {
        value: "Antarctica/DumontDUrville",
        label: "UTC +10:00 DumontDUrville",
      },
      { value: "Antarctica/Macquarie", label: "UTC +11:00 Macquarie" },
      {
        value: "Antarctica/McMurdo",
        label: "UTC +13:00 McMurdo, South Pole",
      },
    ],
  },
  {
    group: "Arctic",
    zones: [{ value: "Arctic/Longyearbyen", label: "UTC +01:00 Longyearbyen" }],
  },
  {
    group: "Asia",
    zones: [
      { value: "Asia/Beirut", label: "UTC +02:00 Beirut" },
      { value: "Asia/Famagusta", label: "UTC +02:00 Famagusta, Nicosia" },
      { value: "Asia/Gaza", label: "UTC +02:00 Gaza, Hebron" },
      { value: "Asia/Jerusalem", label: "UTC +02:00 Jerusalem, Tel Aviv" },
      {
        value: "Asia/Aden",
        label: "UTC +03:00 Baghdad, Bahrain, Istanbul, Qatar",
      },
      { value: "Asia/Tehran", label: "UTC +03:30 Tehran" },
      { value: "Asia/Baku", label: "UTC +04:00 Baku, Dubai, Muscat, Tbilisi" },
      { value: "Asia/Kabul", label: "UTC +04:30 Kabul" },
      { value: "Asia/Yerevan", label: "UTC +04:00 Yerevan" },
      {
        value: "Asia/Almaty",
        label: "UTC +05:00 Almaty, Aqtau, Aqtobe, Ashgabat",
      },
      {
        value: "Asia/Calcutta",
        label: "UTC +05:30 New Delhi, Mumbai, Calcutta",
      },
      { value: "Asia/Kathmandu", label: "UTC +05:45 Kathmandu, Katmandu" },
      {
        value: "Asia/Bishkek",
        label: "UTC +06:00 Bishkek, Dacca, Dhaka, Kashgar",
      },
      { value: "Asia/Rangoon", label: "UTC +06:30 Rangoon, Yangon" },
      { value: "Asia/Bangkok", label: "UTC +07:00 Indochina Time" },
      {
        value: "Asia/Brunei",
        label: "UTC +08:00 China, Hong Kong, Singapore",
      },
      { value: "Asia/Chita", label: "UTC +09:00 Seoul, Tokyo" },
      { value: "Asia/Ust-Nera", label: "UTC +10:00 Ust-Nera, Vladivostok" },
      {
        value: "Asia/Magadan",
        label: "UTC +11:00 Magadan, Sakhalin, Srednekolymsk",
      },
      { value: "Asia/Anadyr", label: "UTC +12:00 Anadyr, Kamchatka" },
    ],
  },
  {
    group: "Atlantic",
    zones: [
      { value: "Atlantic/Bermuda", label: "UTC -04:00 Bermuda" },
      { value: "Atlantic/Stanley", label: "UTC -03:00 Stanley" },
      { value: "Atlantic/South_Georgia", label: "UTC -02:00 South Georgia" },
      { value: "Atlantic/Azores", label: "UTC -01:00 Azores" },
      { value: "Atlantic/Cape_Verde", label: "UTC -01:00 Cape Verde" },
      {
        value: "Atlantic/Canary",
        label: "UTC +00:00 Canary, Faeroe, Faroe, Madeira",
      },
      {
        value: "Atlantic/Reykjavik",
        label: "UTC +00:00 Reykjavik, St Helena",
      },
      { value: "Atlantic/Jan_Mayen", label: "UTC +01:00 Jan Mayen" },
    ],
  },
  {
    group: "Australia",
    zones: [
      { value: "Australia/Eucla", label: "UTC +08:45 Eucla" },
      { value: "Australia/Perth", label: "UTC +08:00 Perth, West" },
      { value: "Australia/Darwin", label: "UTC +09:30 Darwin, North" },
      {
        value: "Australia/Adelaide",
        label: "UTC +10:30 Adelaide, Broken Hill, South, Yancowinna",
      },
      {
        value: "Australia/Brisbane",
        label: "UTC +10:00 Brisbane, Lindeman, Queensland",
      },
      {
        value: "Australia/ACT",
        label: "UTC +11:00 Australian Eastern Time",
      },
      { value: "Australia/LHI", label: "UTC +11:00 LHI, Lord Howe" },
    ],
  },
  {
    group: "Brazil",
    zones: [
      { value: "Brazil/Acre", label: "UTC -05:00 Acre" },
      { value: "Brazil/West", label: "UTC -04:00 West" },
      { value: "Brazil/East", label: "UTC -03:00 East" },
      { value: "Brazil/DeNoronha", label: "UTC -02:00 DeNoronha" },
    ],
  },
  {
    group: "Chile",
    zones: [
      { value: "Chile/EasterIsland", label: "UTC -05:00 EasterIsland" },
      { value: "Chile/Continental", label: "UTC -03:00 Continental" },
    ],
  },
  {
    group: "Indian",
    zones: [
      {
        value: "Indian/Antananarivo",
        label: "UTC +03:00 Antananarivo, Comoro, Mayotte",
      },
      { value: "Indian/Mahe", label: "UTC +04:00 Mahe, Mauritius, Reunion" },
      { value: "Indian/Kerguelen", label: "UTC +05:00 Kerguelen, Maldives" },
      { value: "Indian/Chagos", label: "UTC +06:00 Chagos" },
      { value: "Indian/Cocos", label: "UTC +06:30 Cocos" },
      { value: "Indian/Christmas", label: "UTC +07:00 Christmas" },
    ],
  },
  {
    group: "Mexico",
    zones: [
      { value: "Mexico/BajaNorte", label: "UTC -08:00 BajaNorte" },
      { value: "Mexico/BajaSur", label: "UTC -07:00 BajaSur" },
      { value: "Mexico/General", label: "UTC -06:00 General" },
    ],
  },
  {
    group: "Pacific",
    zones: [
      {
        value: "Pacific/Midway",
        label: "UTC -11:00 Midway, Niue, Pago Pago, Samoa",
      },
      {
        value: "Pacific/Honolulu",
        label: "UTC -10:00 Honolulu, Johnston, Rarotonga, Tahiti",
      },
      { value: "Pacific/Gambier", label: "UTC -09:00 Gambier" },
      { value: "Pacific/Marquesas", label: "UTC -09:30 Marquesas" },
      { value: "Pacific/Pitcairn", label: "UTC -08:00 Pitcairn" },
      { value: "Pacific/Galapagos", label: "UTC -06:00 Galapagos" },
      { value: "Pacific/Easter", label: "UTC -05:00 Easter" },
      { value: "Pacific/Palau", label: "UTC +09:00 Palau" },
      {
        value: "Pacific/Chuuk",
        label: "UTC +10:00 Chuuk, Guam, Port Moresby, Saipan",
      },
      {
        value: "Pacific/Bougainville",
        label: "UTC +11:00 Bougainville, Efate, Guadalcanal, Kosrae",
      },
      {
        value: "Pacific/Fiji",
        label: "UTC +12:00 Fiji, Funafuti, Kwajalein, Majuro",
      },
      { value: "Pacific/Norfolk", label: "UTC +12:00 Norfolk" },
      {
        value: "Pacific/Apia",
        label: "UTC +13:00 Apia, Enderbury, Fakaofo, Kanton",
      },
      { value: "Pacific/Auckland", label: "UTC +13:00 Auckland" },
      { value: "Pacific/Chatham", label: "UTC +13:45 Chatham" },
      { value: "Pacific/Kiritimati", label: "UTC +14:00 Kiritimati" },
    ],
  },
];

/**
 * Find the best matching HubSpot timezone for a given IANA timezone
 * @param detectedTimezone - The timezone detected from the browser
 * @returns The closest matching HubSpot timezone value
 */
export function findMatchingHubSpotTimezone(detectedTimezone: string): string {
  // Flatten all timezone values from HUBSPOT_TIMEZONES
  const allTimezones = HUBSPOT_TIMEZONES.flatMap((group) =>
    group.zones.map((tz) => tz.value)
  );

  // Check for exact match first
  if (allTimezones.includes(detectedTimezone)) {
    return detectedTimezone;
  }

  // Common IANA timezone mappings to HubSpot timezones
  const timezoneMap: Record<string, string> = {
    // US timezones
    "America/Los_Angeles": "US/Pacific",
    "America/Denver": "US/Mountain",
    "America/Phoenix": "US/Mountain",
    "America/Chicago": "US/Central",
    "America/New_York": "US/East-Indiana",
    "America/Indianapolis": "US/East-Indiana",

    // European timezones
    "Europe/London": "Europe/Belfast",
    "Europe/Dublin": "Europe/Belfast",
    "Europe/Lisbon": "Europe/Belfast",
    "Europe/Madrid": "Europe/Amsterdam",
    "Europe/Paris": "Europe/Amsterdam",
    "Europe/Berlin": "Europe/Amsterdam",
    "Europe/Rome": "Europe/Amsterdam",
    "Europe/Brussels": "Europe/Amsterdam",
    "Europe/Vienna": "Europe/Amsterdam",
    "Europe/Warsaw": "Europe/Amsterdam",
    "Europe/Stockholm": "Europe/Amsterdam",
    "Europe/Copenhagen": "Europe/Amsterdam",
    "Europe/Zurich": "Europe/Amsterdam",
    "Europe/Moscow": "Europe/Istanbul",
    "Europe/Minsk": "Europe/Istanbul",

    // Asian timezones
    "Asia/Hong_Kong": "Asia/Brunei",
    "Asia/Shanghai": "Asia/Brunei",
    "Asia/Singapore": "Asia/Brunei",
    "Asia/Kolkata": "Asia/Calcutta",
    "Asia/Seoul": "Asia/Chita",
    "Asia/Tokyo": "Asia/Chita",

    // Australian timezones
    "Australia/Sydney": "Australia/ACT",
    "Australia/Melbourne": "Australia/ACT",
    "Australia/Canberra": "Australia/ACT",

    // Canadian timezones
    "America/Vancouver": "Canada/Pacific",
    "America/Edmonton": "Canada/Mountain",
    "America/Winnipeg": "Canada/Central",
    "America/Regina": "Canada/Saskatchewan",
    "America/Toronto": "Canada/Eastern",
    "America/Halifax": "Canada/Atlantic",
    "America/St_Johns": "Canada/Newfoundland",
  };

  // Try mapped timezone
  if (timezoneMap[detectedTimezone]) {
    return timezoneMap[detectedTimezone];
  }

  // If no mapping found, try to find a timezone in the same region
  const region = detectedTimezone.split("/")[0];
  const regionalMatch = allTimezones.find((tz) => tz.startsWith(region + "/"));
  if (regionalMatch) {
    return regionalMatch;
  }

  // Default fallback to US Central Time
  return "US/Central";
}

export interface TimeSlot {
  startTime: string;
  endTime: string;
}

export interface ParsedAvailability {
  dates: Date[];
  timeSlots: Record<string, Array<{ start: Date; end: Date }>>;
}

export interface AvailableTimesResponse {
  success: boolean;
  data?: {
    availableDates?: string[];
    timeSlots?: Record<string, TimeSlot[]>;
    [key: string]: any;
  };
  error?: string;
}

/**
 * Fetch available meeting times from HubSpot
 * @param timezone - User's timezone (e.g., "America/New_York")
 * @param monthOffset - Optional offset for future months
 * @param meetingLinkSlug - Optional meeting link slug (defaults to env variable)
 * @returns Promise that resolves to available times data
 */
export async function fetchAvailableTimes(
  timezone: string,
  monthOffset?: number,
  meetingLinkSlug?: string
): Promise<AvailableTimesResponse> {
  try {
    const params = new URLSearchParams({ timezone });
    if (monthOffset !== undefined) {
      params.append("monthOffset", monthOffset.toString());
    }
    if (meetingLinkSlug) {
      params.append("meetingLinkSlug", meetingLinkSlug);
    }

    const response = await fetch(
      `/api/hubspot/available-times?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to fetch available times");
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Something went wrong",
    };
  }
}

/**
 * Parse HubSpot availability response and extract dates and time slots
 * @param hubspotData - Raw response data from HubSpot API
 * @param timezone - User's timezone for organizing slots by date
 * @returns Parsed dates and time slots organized by date in the user's timezone
 */
export function parseAvailabilityData(
  hubspotData: any,
  timezone: string
): ParsedAvailability {
  const timeSlotsMap: Record<string, Array<{ start: Date; end: Date }>> = {};
  const uniqueDates = new Set<string>();

  // Parse the linkAvailabilityByDuration object
  if (hubspotData.linkAvailability?.linkAvailabilityByDuration) {
    const availabilityByDuration =
      hubspotData.linkAvailability.linkAvailabilityByDuration;

    // Iterate through each duration (e.g., "1800000" = 30 minutes)
    Object.values(availabilityByDuration).forEach((durationData: any) => {
      if (durationData.availabilities) {
        // Iterate through available slots
        durationData.availabilities.forEach((slot: any) => {
          const startDate = new Date(slot.startMillisUtc);
          const endDate = new Date(slot.endMillisUtc);

          // Format date in the user's timezone
          const dateKey = new Intl.DateTimeFormat("en-CA", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
            .format(startDate)
            .split("/")
            .reverse()
            .join("-");

          uniqueDates.add(dateKey);

          if (!timeSlotsMap[dateKey]) {
            timeSlotsMap[dateKey] = [];
          }

          timeSlotsMap[dateKey].push({
            start: startDate,
            end: endDate,
          });
        });
      }
    });
  }

  // Convert unique dates to Date objects and sort
  const dates = Array.from(uniqueDates)
    .sort()
    .map((dateStr) => new Date(dateStr + "T00:00:00"));

  return {
    dates,
    timeSlots: timeSlotsMap,
  };
}

export interface BookMeetingParams {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  startTime: string;
  endTime: string;
  timezone: string;
  challenges?: string[];
  message?: string;
  meetingLinkSlug?: string;
}

export interface BookMeetingResponse {
  success: boolean;
  message: string;
  data?: {
    contactId: string;
    meetingId: string;
  };
}

/**
 * Book a meeting with HubSpot
 * @param params - Meeting booking parameters
 * @returns Promise that resolves to the booking response
 */
export async function bookMeeting(
  params: BookMeetingParams
): Promise<BookMeetingResponse> {
  try {
    const response = await fetch("/api/hubspot/book-meeting", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Failed to book meeting");
    }

    return {
      success: true,
      message: data.message || "Meeting booked successfully",
      data: data.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Something went wrong",
    };
  }
}
